/**
 * Module dependencies.
 */

import { reduce } from 'lodash';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from 'app/lib/utils';
import React from 'react';
import { Select } from 'app/components/atoms/forms/fields/select/select';

/**
 * `Pagination` type.
 */

export type Pagination = {
  total: number;
  pageSize: number;
  pageSizeOptions: number[];
};

/**
 * `Props` type.
 */

type Props = {
  pagination: Pagination;
  columnsLength: number;
  changePageSize?: (value: number) => void;
};

/**
 * A single pagination cell (page number or nav arrow).
 */

function PageButton({
  children,
  active,
  disabled,
  onClick
}: {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      className={cn(
        'flex h-8 min-w-[32px] items-center justify-center rounded-md border px-2 text-sm tabular-nums transition-colors',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-muted',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent'
      )}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      type={'button'}
    >
      {children}
    </button>
  );
}

/**
 * Export `TablePagination` component.
 */

export function TablePagination(props: Props): JSX.Element {
  const { pagination, columnsLength, changePageSize } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const { total, pageSize } = pagination;
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const pages = Math.ceil(total / pageSize);

  const items = useMemo(() => {
    const result = Array.from({ length: pages }, (_, i) => i + 1);

    return reduce(
      result,
      (acc: any, item: any) => {
        if (item < 4) {
          return [...acc, item];
        }

        if (item > page - 2 && item < page + 2) {
          return [...acc, item];
        }

        if (item >= result.length - 2) {
          return [...acc, item];
        }

        if (item >= 5 && item <= result.length - 3 && acc[acc.length - 1] !== '...') {
          return [...acc, '...'];
        }

        return acc;
      },
      []
    );
  }, [page, pages]);

  const goTo = (value: number) => {
    setSearchParams(params => {
      params.set('page', String(Math.min(Math.max(value, 1), pages)));

      return params;
    });
  };

  return (
    <div
      className={'flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-5'}
      style={{ gridColumn: `1 / ${columnsLength + 1}` }}
    >
      {/* Rows per page */}
      {!!changePageSize ? (
        <div className={'w-[80px]'}>
          <Select
            fieldClassName={'h-8 min-h-8'}
            hideError
            minimal
            name={'pageOptions'}
            onChange={changePageSize}
            options={pagination.pageSizeOptions.map(item => ({ label: String(item), value: item }))}
            value={pagination.pageSize}
          />
        </div>
      ) : (
        <span />
      )}

      {/* Page navigation */}
      {pages > 1 && (
        <div className={'flex items-center gap-1.5'}>
          <PageButton disabled={page <= 1} onClick={() => goTo(1)}>
            {'«'}
          </PageButton>

          <PageButton disabled={page <= 1} onClick={() => goTo(page - 1)}>
            {'‹'}
          </PageButton>

          {items.map((item: any, index: number) => {
            const isPage = !isNaN(parseInt(item, 10));

            return isPage ? (
              <PageButton active={page === item} key={item} onClick={() => goTo(item)}>
                {item}
              </PageButton>
            ) : (
              <span className={'px-1 text-sm text-muted-foreground'} key={`ellipsis-${index}`}>
                {'…'}
              </span>
            );
          })}

          <PageButton disabled={page >= pages} onClick={() => goTo(page + 1)}>
            {'›'}
          </PageButton>

          <PageButton disabled={page >= pages} onClick={() => goTo(pages)}>
            {'»'}
          </PageButton>
        </div>
      )}
    </div>
  );
}
